import React, { useEffect, useState, useRef } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api, setToken, clearToken, getToken, resolveImageUrl, printInternalCennik, INTERNAL_PRINT_SECTIONS } from "@/lib/api";
import { LogOut, Save, Plus, Trash2, ArrowUp, ArrowDown, Loader2, Lock, Upload, Download, Image as ImageIcon, Printer } from "lucide-react";

/* Defaulty tekstów konfiguratora — wyświetlane jako wartości pól gdy DB jeszcze ich nie ma.
   Synchronizuj z DEFAULT_TEXTS w /app/frontend/src/Configurator.js */
const CONFIGURATOR_DEFAULTS = {
  sectionTitle: "Skonfiguruj swoje ogrodzenie panelowe.",
  sectionDescription: "Wpisz długość, wybierz panel i wysokość — kalkulator policzy ilość paneli, słupków, podmurówek, łączników i obejm. Otrzymasz orientacyjną wycenę gotową do wydruku.",
  ctaButton: "Skonfiguruj własne ogrodzenie panelowe",
  ctaSubtext: "Otrzymasz orientacyjną wycenę gotową do wydruku",
  closeButton: "← Zwiń konfigurator",
  step1Title: "Krok 1 — Długość", step1Hint: "Łączna długość ogrodzenia",
  step2Title: "Krok 2 — Kolor / wykończenie", step2Hint: "Wybierz RAL (malowane proszkowo) lub OCYNK (powłoka cynkowa, bez koloru)",
  step3Title: "Krok 3 — Podmurówka", step3Hint: "Czy ogrodzenie ma stać na betonowej podmurówce 25 cm?",
  step3PodmYes: "Z podmurówką", step3PodmNo: "Bez podmurówki",
  step3ColorLabel: "Kolor podmurówki", step3PatternLabel: "Wzór podmurówki", step3LacznikLabel: "Łącznik podmurówki",
  step4Title: "Krok 4 — Panel", step4Hint: "Grubość drutu paneli (wykończenie RAL/OCYNK ustawiasz w Kroku 2)",
  step4ThicknessLabel: "Grubość drutu", step4HeightLabel: "Wysokość panelu (cm)",
  step5Title: "Krok 5 — Grubość słupka", step5Hint: "Im grubsza ścianka, tym sztywniejszy słupek",
  step6Title: "Krok 6 — Wysokość słupka",
  step6HintTemplate: "Sugerowana: {sug} m (panel {h} cm{podm} + 50 cm w gruncie)",
  step6PodmAddon: " + podmurówka 25 cm",
  step6Warning: "⚠ Słupek za krótki. Dla panelu {h} cm{podm} potrzeba minimum {min} cm. Zalecamy słupek {sug} m.",
  step7Title: "Krok 7 — Obejmy na słupek",
  step7HintTemplate: "Sugerowana ilość: {n} szt./słupek (panel {comparator} 153 cm)",
  notesLabel: "Uwagi do wyceny (opcjonalnie)",
  notesPlaceholder: "Dodaj informacje, które pomogą w finalizacji wyceny — narożniki, obejmy końcowe, miejsce ewentualnego transportu, utrudnienia itp.",
  quoteTitle: "Twoja wycena",
  quoteEmpty: "Wpisz długość ogrodzenia, aby zobaczyć wycenę.",
  sumaLabel: "Suma",
  quoteFooter: "Wycena orientacyjna · ceny brutto · bez kosztu transportu i\u00a0montażu",
  callButton: "Zadzwoń",
  printButton: "Zapisz / Drukuj wycenę",
  printHint: "W oknie wydruku wybierz „Zapisz jako PDF\", by pobrać plik na dysk",
  quoteDocTitle: "Wycena ogrodzenia panelowego",
};

/* ─────────── LOGIN ─────────── */
export const AdminLogin = () => {
  const nav = useNavigate();
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const r = await api.login(u, p);
      setToken(r.token);
      nav("/admin", { replace: true });
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Błąd logowania");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center p-6">
      <form onSubmit={submit} className="w-full max-w-sm" data-testid="admin-login-form">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-[#ea580c] flex items-center justify-center">
            <Lock className="text-white" size={22}/>
          </div>
          <div>
            <div className="font-display text-white text-2xl uppercase tracking-wider">Panel Admina</div>
            <div className="font-mono text-[10px] text-[#a8a29e] uppercase tracking-[0.3em]">Ogrodzenia Fajsławice</div>
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-[#a8a29e] mb-1.5">Login</label>
            <input value={u} onChange={(e) => setU(e.target.value)} className="w-full bg-[#131316] border border-[#2a2a2e] px-4 py-3 text-white focus:border-[#ea580c] focus:outline-none" data-testid="admin-username" autoComplete="username" />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-[#a8a29e] mb-1.5">Hasło</label>
            <input type="password" value={p} onChange={(e) => setP(e.target.value)} className="w-full bg-[#131316] border border-[#2a2a2e] px-4 py-3 text-white focus:border-[#ea580c] focus:outline-none" data-testid="admin-password" autoComplete="current-password"/>
          </div>
          {err && <div className="text-sm text-[#ea580c] py-2" data-testid="admin-error">{err}</div>}
          <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="admin-login-submit">
            {busy ? <Loader2 className="animate-spin" size={16}/> : <Lock size={16}/>} Zaloguj
          </button>
        </div>
      </form>
    </div>
  );
};

/* ─────────── ADMIN DASHBOARD ─────────── */
const Section = ({ title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-[#2a2a2e] mb-4 bg-[#0d0d0f]">
      <button onClick={() => setOpen(!open)} className="w-full px-6 py-4 flex items-center justify-between hover:bg-[#131316] text-left">
        <span className="font-display text-xl uppercase text-white tracking-wider">{title}</span>
        <span className="font-mono text-xs text-[#ea580c]">{open ? "−" : "+"}</span>
      </button>
      {open && <div className="px-6 pb-6 pt-2 border-t border-[#2a2a2e]">{children}</div>}
    </div>
  );
};

const Field = ({ label, value, onChange, multiline = false, rows = 2, mono = false, placeholder = "", hint = "" }) => (
  <label className="block mb-3">
    <span className="block text-[10px] font-mono uppercase tracking-widest text-[#a8a29e] mb-1">{label}</span>
    {multiline ? (
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className={`w-full bg-[#131316] border border-[#2a2a2e] px-3 py-2 text-white text-sm focus:border-[#ea580c] focus:outline-none ${mono ? "font-mono" : ""}`}
      />
    ) : (
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full bg-[#131316] border border-[#2a2a2e] px-3 py-2 text-white text-sm focus:border-[#ea580c] focus:outline-none ${mono ? "font-mono" : ""}`}
      />
    )}
    {hint && <span className="block text-[10px] text-[#57534e] font-mono mt-1 leading-snug">{hint}</span>}
  </label>
);

/* ─────────── IMAGE PICKER ─────────── */
const ImagePicker = ({ label, value, onChange, hint = "" }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setErr("");
    try {
      const r = await api.uploadImage(file);
      onChange(r.url);
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Błąd uploadu");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <div className="mb-4">
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#a8a29e] mb-2">{label}</div>
      <div className="flex items-start gap-4">
        <div className="w-32 h-24 bg-[#131316] border border-[#2a2a2e] flex items-center justify-center overflow-hidden shrink-0">
          {value ? (
            <img src={resolveImageUrl(value)} alt="" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon className="text-[#57534e]" size={28}/>
          )}
        </div>
        <div className="flex-1">
          <input ref={ref} type="file" accept="image/*" onChange={handle} className="hidden" />
          <button type="button" onClick={() => ref.current?.click()} disabled={busy}
                  className="px-4 py-2 border border-[#2a2a2e] hover:border-[#ea580c] hover:text-[#ea580c] text-white text-xs font-mono uppercase tracking-widest inline-flex items-center gap-2">
            {busy ? <Loader2 className="animate-spin" size={14}/> : <Upload size={14}/>}
            {value ? "Zmień obrazek" : "Wgraj obrazek"}
          </button>
          {value && (
            <button type="button" onClick={() => onChange("")} disabled={busy}
                    className="ml-2 px-4 py-2 border border-[#2a2a2e] hover:border-red-500 hover:text-red-500 text-[#a8a29e] text-xs font-mono uppercase tracking-widest inline-flex items-center gap-2">
              <Trash2 size={14}/> Usuń
            </button>
          )}
          {hint && <div className="text-xs text-[#a8a29e] mt-2">{hint}</div>}
          {err && <div className="text-xs text-red-500 mt-2">{err}</div>}
          {value && <div className="text-[10px] text-[#57534e] mt-2 font-mono break-all">{value}</div>}
        </div>
      </div>
    </div>
  );
};

/* ─────────── DOWNLOAD STATS ─────────── */
const DownloadStats = () => {
  const [s, setS] = useState(null);
  useEffect(() => {
    let alive = true;
    const tick = () => api.getStats().then((d) => alive && setS(d)).catch(() => {});
    tick();
    const i = setInterval(tick, 30000);
    return () => { alive = false; clearInterval(i); };
  }, []);
  if (!s) return null;
  const cells = [
    { k: "Dziś", v: s.today },
    { k: "Tydzień", v: s.week },
    { k: "Miesiąc", v: s.month },
    { k: "Rok", v: s.year },
    { k: "Łącznie", v: s.total },
  ];
  return (
    <div className="border border-[#2a2a2e] bg-[#0d0d0f] mb-6" data-testid="download-stats">
      <div className="px-6 py-3 border-b border-[#2a2a2e] bg-[#131316] flex items-center gap-3">
        <Download className="text-[#ea580c]" size={18}/>
        <span className="font-display text-lg uppercase text-white tracking-wider">Pobrania cennika</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[#2a2a2e]">
        {cells.map((c, i) => (
          <div key={i} className="bg-[#0a0a0b] px-6 py-5">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#a8a29e]">{c.k}</div>
            <div className="font-display text-3xl text-[#ea580c] mt-1">{c.v}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ConfigEditor = ({ config, onSave }) => {
  const [c, setC] = useState(config);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const set = (path) => (v) => {
    setC((cur) => {
      const copy = JSON.parse(JSON.stringify(cur));
      const keys = path.split(".");
      let o = copy;
      for (let i = 0; i < keys.length - 1; i++) {
        if (o[keys[i]] == null || typeof o[keys[i]] !== "object") {
          o[keys[i]] = {};
        }
        o = o[keys[i]];
      }
      o[keys[keys.length - 1]] = v;
      return copy;
    });
  };

  const save = async () => {
    setSaving(true); setMsg("");
    try {
      await onSave(c);
      setMsg("✓ Zapisano");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) {
      setMsg("Błąd: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="config-editor">
      {/* Grafiki */}
      <Section title="Grafiki strony" defaultOpen>
        <ImagePicker
          label='Logo (zastępuje pomarańczowy kwadrat „OF")'
          value={c.images?.logo}
          onChange={(v) => setC({...c, images: {...(c.images||{}), logo: v}})}
          hint="Sugerowane: PNG/SVG z przezroczystym tłem, kwadrat 200×200 px lub większy."
        />
        <ImagePicker
          label="Tło sekcji Hero"
          value={c.images?.heroBg}
          onChange={(v) => setC({...c, images: {...(c.images||{}), heroBg: v}})}
          hint="Najlepsze proporcje: 16:9, min. 1600 px szerokości."
        />
        <ImagePicker
          label='Tło sekcji „O firmie"'
          value={c.images?.aboutBg}
          onChange={(v) => setC({...c, images: {...(c.images||{}), aboutBg: v}})}
          hint="Najlepsze proporcje: 16:9, min. 1600 px szerokości."
        />
      </Section>

      {/* Firma */}
      <Section title="Dane firmy & kontakt">
        <div className="grid md:grid-cols-2 gap-x-6">
          <Field label="Nazwa firmy" value={c.company?.name} onChange={set("company.name")} />
          <Field label="Właściciel" value={c.company?.owner} onChange={set("company.owner")} />
          <Field label="NIP" value={c.company?.nip} onChange={set("company.nip")} />
          <Field label="E-mail" value={c.company?.email} onChange={set("company.email")} />
          <Field label="Adres" value={c.company?.address} onChange={set("company.address")} />
          <Field label="Region" value={c.company?.region} onChange={set("company.region")} />
          <Field label="GPS" value={c.company?.gps} onChange={set("company.gps")} mono />
          <Field label="Link do Google Maps" value={c.company?.mapsUrl} onChange={set("company.mapsUrl")} />
          <Field label="Telefon 1 — numer" value={c.company?.phones?.[0]?.num} onChange={(v) => {
            const ph = [...(c.company.phones || [])]; ph[0] = {...ph[0], num: v};
            setC({...c, company: {...c.company, phones: ph}});
          }} />
          <Field label="Telefon 1 — imię" value={c.company?.phones?.[0]?.name} onChange={(v) => {
            const ph = [...(c.company.phones || [])]; ph[0] = {...ph[0], name: v};
            setC({...c, company: {...c.company, phones: ph}});
          }} />
          <Field label="Telefon 2 — numer" value={c.company?.phones?.[1]?.num} onChange={(v) => {
            const ph = [...(c.company.phones || [])]; ph[1] = {...ph[1], num: v};
            setC({...c, company: {...c.company, phones: ph}});
          }} />
          <Field label="Telefon 2 — imię" value={c.company?.phones?.[1]?.name} onChange={(v) => {
            const ph = [...(c.company.phones || [])]; ph[1] = {...ph[1], name: v};
            setC({...c, company: {...c.company, phones: ph}});
          }} />
          <Field label="Godziny — Pon-Pt" value={c.company?.hours?.weekdays} onChange={set("company.hours.weekdays")} />
          <Field label="Godziny — Sobota" value={c.company?.hours?.saturday} onChange={set("company.hours.saturday")} />
        </div>
      </Section>

      {/* Hero */}
      <Section title="Sekcja główna (hero)">
        <Field label="Tag pomarańczowy" value={c.hero?.tag} onChange={set("hero.tag")} />
        <Field label="Tag drugi" value={c.hero?.tag2} onChange={set("hero.tag2")} />
        <Field label="Tytuł — linia 1 (pomarańczowa)" value={c.hero?.title_1} onChange={set("hero.title_1")} />
        <Field label="Tytuł — linia 2 (biała)" value={c.hero?.title_2} onChange={set("hero.title_2")} multiline rows={2}/>
        <Field label="Tytuł — linia 3 (kontur)" value={c.hero?.title_3} onChange={set("hero.title_3")} />
        <Field label="Opis pod tytułem" value={c.hero?.description} onChange={set("hero.description")} multiline rows={4}/>
        <div className="mt-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#a8a29e] mb-2">Kafelki (4 sztuki)</div>
          {(c.hero?.stats || []).map((s, i) => (
            <div key={i} className="grid md:grid-cols-3 gap-x-4 gap-y-0 border border-[#2a2a2e] p-3 mb-2">
              <Field label={`#${i+1} Tytuł`} value={s.k} onChange={(v) => { const a = [...c.hero.stats]; a[i] = {...a[i], k:v}; setC({...c, hero: {...c.hero, stats:a}}); }}/>
              <Field label="Podpis" value={s.sub} onChange={(v) => { const a = [...c.hero.stats]; a[i] = {...a[i], sub:v}; setC({...c, hero: {...c.hero, stats:a}}); }}/>
              <Field label="Opis" value={s.desc} onChange={(v) => { const a = [...c.hero.stats]; a[i] = {...a[i], desc:v}; setC({...c, hero: {...c.hero, stats:a}}); }}/>
            </div>
          ))}
        </div>
        <Field label="Pasek lecący (po przecinku)" value={(c.hero?.marquee || []).join(", ")}
               onChange={(v) => setC({...c, hero: {...c.hero, marquee: v.split(",").map(s => s.trim()).filter(Boolean)}})} multiline rows={3}/>
      </Section>

      {/* O firmie */}
      <Section title="O firmie">
        <Field label="Tytuł — przed" value={c.about?.title_1} onChange={set("about.title_1")} />
        <Field label="Tytuł — środek (pomarańcz)" value={c.about?.title_2} onChange={set("about.title_2")} />
        <Field label="Tytuł — koniec (pomarańcz)" value={c.about?.title_3} onChange={set("about.title_3")} />
        {(c.about?.paragraphs || []).map((p, i) => (
          <Field key={i} label={`Akapit ${i+1}`} value={p} onChange={(v) => {
            const a = [...c.about.paragraphs]; a[i] = v; setC({...c, about: {...c.about, paragraphs:a}});
          }} multiline rows={3}/>
        ))}
        <div className="mt-4 text-[10px] font-mono uppercase tracking-widest text-[#a8a29e] mb-2">Kafelki cech (4)</div>
        {(c.about?.features || []).map((f, i) => (
          <div key={i} className="grid md:grid-cols-2 gap-x-4 border border-[#2a2a2e] p-3 mb-2">
            <Field label={`#${i+1} Tytuł`} value={f.title} onChange={(v) => { const a = [...c.about.features]; a[i] = {...a[i], title:v}; setC({...c, about: {...c.about, features:a}}); }}/>
            <Field label="Opis" value={f.desc} onChange={(v) => { const a = [...c.about.features]; a[i] = {...a[i], desc:v}; setC({...c, about: {...c.about, features:a}}); }}/>
          </div>
        ))}
      </Section>

      {/* Produkty */}
      <Section title="Produkty (12 kafelków)">
        {(c.products || []).map((p, i) => (
          <div key={i} className="border border-[#2a2a2e] p-4 mb-3">
            <div className="grid md:grid-cols-3 gap-x-4">
              <Field label={`#${i+1} Ikona`} value={p.icon} onChange={(v) => { const a = [...c.products]; a[i] = {...a[i], icon:v}; setC({...c, products:a}); }}/>
              <Field label="Tytuł" value={p.title} onChange={(v) => { const a = [...c.products]; a[i] = {...a[i], title:v}; setC({...c, products:a}); }}/>
              <Field label="Opis" value={p.blurb} onChange={(v) => { const a = [...c.products]; a[i] = {...a[i], blurb:v}; setC({...c, products:a}); }} multiline rows={2}/>
            </div>
            <ImagePicker
              label="Zdjęcie produktu (opcjonalne — bez niego pokazuje się ikona)"
              value={p.image}
              onChange={(v) => { const a = [...c.products]; a[i] = {...a[i], image:v}; setC({...c, products:a}); }}
              hint="Proporcje 4:3 lub 16:9, min. 800 px szerokości."
            />
          </div>
        ))}
        <p className="text-xs text-[#a8a29e] mt-2 font-mono">Dostępne ikony: Layers, Hammer, Ruler, Zap, Factory, DoorOpen</p>
      </Section>

      {/* Konfigurator */}
      <Section title="Konfigurator — teksty">
        <div className="text-xs text-[#a8a29e] mb-4 font-mono leading-relaxed">
          Edytujesz wszystkie teksty pojawiające się w konfiguratorze ogrodzenia panelowego.<br/>
          Niektóre pola mają placeholdery <code className="text-[#ea580c]">{'{nazwa}'}</code> — zostaw je, są podstawiane automatycznie (np. <code className="text-[#ea580c]">{'{n}'}</code>, <code className="text-[#ea580c]">{'{h}'}</code>, <code className="text-[#ea580c]">{'{sug}'}</code>).
        </div>

        {(() => {
          const D = CONFIGURATOR_DEFAULTS;
          const cfg = c.configurator || {};
          const v = (k) => (cfg[k] != null ? cfg[k] : D[k] || "");
          return (<>
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#ea580c] mb-2">Sekcja główna</div>
        <Field label="Tytuł sekcji (h2)" value={v("sectionTitle")} onChange={set("configurator.sectionTitle")} multiline rows={2}/>
        <Field label="Opis pod tytułem" value={v("sectionDescription")} onChange={set("configurator.sectionDescription")} multiline rows={3}/>
        <Field label="Tekst CTA buttona (gdy konfigurator zwinięty)" value={v("ctaButton")} onChange={set("configurator.ctaButton")}/>
        <Field label="Tekst pod CTA buttonem" value={v("ctaSubtext")} onChange={set("configurator.ctaSubtext")}/>
        <Field label="Przycisk „Zwiń konfigurator”" value={v("closeButton")} onChange={set("configurator.closeButton")}/>

        <div className="text-[10px] font-mono uppercase tracking-widest text-[#ea580c] mt-6 mb-2">Krok 1 — Długość</div>
        <div className="grid md:grid-cols-2 gap-x-4">
          <Field label="Tytuł" value={v("step1Title")} onChange={set("configurator.step1Title")}/>
          <Field label="Hint" value={v("step1Hint")} onChange={set("configurator.step1Hint")}/>
        </div>

        <div className="text-[10px] font-mono uppercase tracking-widest text-[#ea580c] mt-6 mb-2">Krok 2 — Kolor</div>
        <div className="grid md:grid-cols-2 gap-x-4">
          <Field label="Tytuł" value={v("step2Title")} onChange={set("configurator.step2Title")}/>
          <Field label="Hint" value={v("step2Hint")} onChange={set("configurator.step2Hint")} multiline rows={2}/>
        </div>

        <div className="text-[10px] font-mono uppercase tracking-widest text-[#ea580c] mt-6 mb-2">Krok 3 — Podmurówka</div>
        <div className="grid md:grid-cols-2 gap-x-4">
          <Field label="Tytuł" value={v("step3Title")} onChange={set("configurator.step3Title")}/>
          <Field label="Hint" value={v("step3Hint")} onChange={set("configurator.step3Hint")}/>
          <Field label="Przycisk „Z podmurówką”" value={v("step3PodmYes")} onChange={set("configurator.step3PodmYes")}/>
          <Field label="Przycisk „Bez podmurówki”" value={v("step3PodmNo")} onChange={set("configurator.step3PodmNo")}/>
          <Field label="Etykieta — Kolor podmurówki" value={v("step3ColorLabel")} onChange={set("configurator.step3ColorLabel")}/>
          <Field label="Etykieta — Wzór podmurówki" value={v("step3PatternLabel")} onChange={set("configurator.step3PatternLabel")}/>
          <Field label="Etykieta — Łącznik podmurówki" value={v("step3LacznikLabel")} onChange={set("configurator.step3LacznikLabel")}/>
        </div>

        <div className="text-[10px] font-mono uppercase tracking-widest text-[#ea580c] mt-6 mb-2">Krok 4 — Panel</div>
        <div className="grid md:grid-cols-2 gap-x-4">
          <Field label="Tytuł" value={v("step4Title")} onChange={set("configurator.step4Title")}/>
          <Field label="Hint" value={v("step4Hint")} onChange={set("configurator.step4Hint")} multiline rows={2}/>
          <Field label="Etykieta — Grubość drutu" value={v("step4ThicknessLabel")} onChange={set("configurator.step4ThicknessLabel")}/>
          <Field label="Etykieta — Wysokość panelu" value={v("step4HeightLabel")} onChange={set("configurator.step4HeightLabel")}/>
        </div>

        <div className="text-[10px] font-mono uppercase tracking-widest text-[#ea580c] mt-6 mb-2">Krok 5 — Grubość słupka</div>
        <div className="grid md:grid-cols-2 gap-x-4">
          <Field label="Tytuł" value={v("step5Title")} onChange={set("configurator.step5Title")}/>
          <Field label="Hint" value={v("step5Hint")} onChange={set("configurator.step5Hint")}/>
        </div>

        <div className="text-[10px] font-mono uppercase tracking-widest text-[#ea580c] mt-6 mb-2">Krok 6 — Wysokość słupka</div>
        <Field label="Tytuł" value={v("step6Title")} onChange={set("configurator.step6Title")}/>
        <Field
          label="Hint (szablon)"
          value={v("step6HintTemplate")}
          onChange={set("configurator.step6HintTemplate")}
          multiline rows={2}
          hint="Placeholdery: {sug}=sugerowana długość (m), {h}=wysokość panelu, {podm}=tekst dodatku gdy z podmurówką"
        />
        <Field
          label="Tekst dodatku „+ podmurówka 25 cm”"
          value={v("step6PodmAddon")}
          onChange={set("configurator.step6PodmAddon")}
        />
        <Field
          label="Ostrzeżenie (gdy słupek za krótki)"
          value={v("step6Warning")}
          onChange={set("configurator.step6Warning")}
          multiline rows={3}
          hint="Placeholdery: {h}=wysokość panelu, {podm}=dodatek, {min}=wymagane minimum cm, {sug}=sugerowana długość m"
        />

        <div className="text-[10px] font-mono uppercase tracking-widest text-[#ea580c] mt-6 mb-2">Krok 7 — Obejmy</div>
        <Field label="Tytuł" value={v("step7Title")} onChange={set("configurator.step7Title")}/>
        <Field
          label="Hint (szablon)"
          value={v("step7HintTemplate")}
          onChange={set("configurator.step7HintTemplate")}
          multiline rows={2}
          hint="Placeholdery: {n}=ilość obejm/słupek, {comparator}=≥ lub <"
        />

        <div className="text-[10px] font-mono uppercase tracking-widest text-[#ea580c] mt-6 mb-2">Uwagi do wyceny</div>
        <Field label="Etykieta" value={v("notesLabel")} onChange={set("configurator.notesLabel")}/>
        <Field label="Placeholder w polu tekstowym" value={v("notesPlaceholder")} onChange={set("configurator.notesPlaceholder")} multiline rows={3}/>

        <div className="text-[10px] font-mono uppercase tracking-widest text-[#ea580c] mt-6 mb-2">Panel wyceny (po prawej stronie)</div>
        <div className="grid md:grid-cols-2 gap-x-4">
          <Field label="Tytuł panelu („Twoja wycena”)" value={v("quoteTitle")} onChange={set("configurator.quoteTitle")}/>
          <Field label="Etykieta sumy („Suma”)" value={v("sumaLabel")} onChange={set("configurator.sumaLabel")}/>
        </div>
        <Field label="Tekst gdy długość = 0" value={v("quoteEmpty")} onChange={set("configurator.quoteEmpty")}/>
        <Field label="Stopka pod sumą" value={v("quoteFooter")} onChange={set("configurator.quoteFooter")} multiline rows={2}/>
        <div className="grid md:grid-cols-2 gap-x-4">
          <Field label="Przycisk „Zadzwoń”" value={v("callButton")} onChange={set("configurator.callButton")}/>
          <Field label="Przycisk „Drukuj wycenę”" value={v("printButton")} onChange={set("configurator.printButton")}/>
        </div>
        <Field label="Hint pod przyciskami druku" value={v("printHint")} onChange={set("configurator.printHint")} multiline rows={2}/>
        <Field label="Tytuł dokumentu PDF" value={v("quoteDocTitle")} onChange={set("configurator.quoteDocTitle")}/>
          </>);
        })()}
      </Section>

      <Section title="Sekcja Transport (mapa zasięgu + miasta)" defaultOpen>
        {(() => {
          const t = c.transport || {};
          const tv = (k) => (t[k] != null ? t[k] : "");
          const cities = Array.isArray(t.cities) ? t.cities : [];
          // Lista miast, dla których komponent ma gotowe pozycje na mapie SVG.
          const MAP_KNOWN = ["Lublin", "Krasnystaw", "Świdnik", "Chełm", "Zamość", "Łęczna", "Lubartów", "Biłgoraj", "Hrubieszów", "Tomaszów Lub.", "Puławy"];
          return (
            <>
              <div className="bg-[#0d0d0f] border border-[#2a2a2e] p-3 text-xs text-[#a8a29e] mb-4">
                Edytujesz sekcję wyświetlaną między „O firmie" a „Produkty". Lista miast pojawia się na mapie SVG (jeśli miasto jest na liście obsługiwanych pozycji) oraz jako chipy pod mapą. Wszystkie miasta trafiają również do <b className="text-white">schema.org</b> jako <code>areaServed</code> — pomaga w lokalnym SEO Google.
                <br/><span className="text-[#ea580c]">Miasta z pozycją na mapie:</span> {MAP_KNOWN.join(", ")}. Inne miasta pojawią się tylko jako chipy + schema.
              </div>

              <Field label={`Tytuł (H2 w nagłówku, np. "Transport")`} value={tv("heading")} onChange={set("transport.heading")} />
              <Field label="Podtytuł (linia pod H2)" value={tv("subheading")} onChange={set("transport.subheading")} />
              <Field label="Treść (2 akapity oddzielone pustą linią)" value={tv("body")} onChange={set("transport.body")} multiline rows={6} />

              <div className="grid md:grid-cols-3 gap-x-4">
                <Field label="Tekst przycisku CTA" value={tv("ctaText")} onChange={set("transport.ctaText")} />
                <Field label="Telefon CTA #1" value={tv("ctaPhone")} onChange={set("transport.ctaPhone")} />
                <Field label="Telefon CTA #2 (opcjonalnie)" value={tv("ctaPhone2")} onChange={set("transport.ctaPhone2")} />
              </div>

              <div className="text-[10px] font-mono uppercase tracking-widest text-[#ea580c] mt-4 mb-2">Miasta w obszarze dostaw</div>
              <div className="space-y-2">
                {cities.map((city, i) => (
                  <div key={i} className="flex gap-2 items-center bg-[#0d0d0f] border border-[#2a2a2e] p-2" data-testid={`transport-city-${i}`}>
                    <input
                      value={city}
                      onChange={(e) => {
                        const next = [...cities]; next[i] = e.target.value;
                        set("transport.cities")(next);
                      }}
                      placeholder="np. Lublin"
                      className="flex-1 bg-[#0a0a0b] border border-[#2a2a2e] px-3 py-2 text-sm text-white"
                    />
                    {MAP_KNOWN.includes(city) ? (
                      <span className="text-[10px] font-mono uppercase tracking-widest text-[#22c55e]" title="Miasto pojawi się na mapie SVG">na mapie</span>
                    ) : (
                      <span className="text-[10px] font-mono uppercase tracking-widest text-[#a8a29e]" title="Tylko chip + schema">tylko chip</span>
                    )}
                    <button
                      onClick={() => set("transport.cities")(cities.filter((_, k) => k !== i))}
                      className="text-[#ea580c] hover:bg-[#ea580c]/10 p-2"
                      data-testid={`transport-city-del-${i}`}
                    ><Trash2 size={16}/></button>
                  </div>
                ))}
                <button
                  onClick={() => set("transport.cities")([...cities, ""])}
                  className="text-xs font-mono uppercase tracking-widest text-[#ea580c] hover:underline"
                  data-testid="transport-city-add"
                ><Plus size={14} className="inline"/> Dodaj miasto</button>
              </div>
            </>
          );
        })()}
      </Section>

      <div className="sticky bottom-4 bg-[#0a0a0b] border border-[#ea580c] p-4 flex items-center justify-between mt-6">
        {msg && <span className="text-sm text-[#ea580c] font-mono">{msg}</span>}
        <button onClick={save} disabled={saving} className="btn-primary ml-auto" data-testid="save-config-btn">
          {saving ? <Loader2 className="animate-spin" size={16}/> : <Save size={16}/>} Zapisz wszystkie zmiany
        </button>
      </div>
    </div>
  );
};

const PricingEditor = ({ initial, onChanged }) => {
  const [tab, setTab] = useState("siatka");
  const [data, setData] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printSel, setPrintSel] = useState(() => INTERNAL_PRINT_SECTIONS.map((s) => s.id));
  const [customPages, setCustomPages] = useState([]); // [{id, categoryIds:[]}]
  const [fontScale, setFontScale] = useState(1.0);

  const FONT_PRESETS = [
    { id: "s",  label: "Mała",       scale: 0.85 },
    { id: "m",  label: "Średnia",    scale: 1.0 },
    { id: "l",  label: "Duża",       scale: 1.2 },
    { id: "xl", label: "Bardzo duża", scale: 1.4 },
  ];

  const cats = data[tab] || [];

  const togglePrintSection = (id) => {
    setPrintSel((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const addCustomPage = () => {
    setCustomPages((prev) => [...prev, { id: `custom-${Date.now()}`, categoryIds: [] }]);
  };
  const removeCustomPage = (id) => {
    setCustomPages((prev) => prev.filter((p) => p.id !== id));
  };
  const toggleCustomCat = (pageId, catId) => {
    setCustomPages((prev) => prev.map((p) => {
      if (p.id !== pageId) return p;
      const has = p.categoryIds.includes(catId);
      return { ...p, categoryIds: has ? p.categoryIds.filter((x) => x !== catId) : [...p.categoryIds, catId] };
    }));
  };
  const totalPagesToPrint =
    printSel.length +
    (customPages || []).filter((cp) => (cp.categoryIds || []).length > 0).length;

  const runInternalPrint = async () => {
    const company = (await api.getConfig())?.company || {};
    printInternalCennik(data, printSel, customPages, company, fontScale);
    setShowPrintModal(false);
  };

  const updateCat = (idx, patch) => {
    const updated = [...cats]; updated[idx] = {...updated[idx], ...patch};
    setData({...data, [tab]: updated});
  };

  const addItem = (idx) => {
    const updated = [...cats];
    updated[idx] = {...updated[idx], items: [...(updated[idx].items || []), {n: "Nowa pozycja", p: "0 zł"}]};
    setData({...data, [tab]: updated});
  };

  const removeItem = (catIdx, itemIdx) => {
    const updated = [...cats];
    updated[catIdx] = {...updated[catIdx], items: updated[catIdx].items.filter((_, i) => i !== itemIdx)};
    setData({...data, [tab]: updated});
  };

  const setItemField = (catIdx, itemIdx, key, val) => {
    const updated = [...cats];
    const items = [...updated[catIdx].items];
    items[itemIdx] = {...items[itemIdx], [key]: val};
    updated[catIdx] = {...updated[catIdx], items};
    setData({...data, [tab]: updated});
  };

  const saveCat = async (idx) => {
    const cat = cats[idx];
    setBusy(true); setMsg("");
    try {
      await api.updateCategory(cat.id, {name: cat.name, items: cat.items});
      setMsg(`✓ Zapisano: ${cat.name}`);
      setTimeout(() => setMsg(""), 2500);
      onChanged && onChanged();
    } catch (e) {
      setMsg("Błąd: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const deleteCat = async (idx) => {
    if (!window.confirm(`Usunąć kategorię "${cats[idx].name}"?`)) return;
    setBusy(true);
    try {
      await api.deleteCategory(cats[idx].id);
      setData({...data, [tab]: cats.filter((_, i) => i !== idx)});
      setMsg("✓ Usunięto");
      onChanged && onChanged();
    } catch (e) {
      setMsg("Błąd: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const addCat = async () => {
    setBusy(true);
    try {
      const c = await api.addCategory(tab, {name: "Nowa kategoria", items: []});
      setData({...data, [tab]: [...cats, c]});
      setMsg("✓ Dodano kategorię");
    } catch (e) {
      setMsg("Błąd: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const move = async (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= cats.length) return;
    const reordered = [...cats];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    setData({...data, [tab]: reordered});
    try {
      await api.reorder(tab, reordered.map(c => c.id));
      onChanged && onChanged();
    } catch (e) { setMsg("Błąd zmiany kolejności"); }
  };

  return (
    <div data-testid="pricing-editor">
      <div className="flex flex-wrap gap-3 mb-6 items-center">
        <div className="flex gap-px bg-[#2a2a2e] border border-[#2a2a2e] flex-1 min-w-[280px]">
          <button onClick={() => setTab("siatka")} className={`flex-1 px-6 py-4 ${tab === "siatka" ? "bg-[#ea580c] text-white" : "bg-[#0d0d0f] text-[#a8a29e]"}`} data-testid="admin-tab-siatka">
            Ogrodzenia z siatki
          </button>
          <button onClick={() => setTab("panele")} className={`flex-1 px-6 py-4 ${tab === "panele" ? "bg-[#ea580c] text-white" : "bg-[#0d0d0f] text-[#a8a29e]"}`} data-testid="admin-tab-panele">
            Ogrodzenia panelowe
          </button>
        </div>
        <button
          onClick={() => setShowPrintModal(true)}
          data-testid="internal-print-btn"
          className="px-5 py-4 bg-[#0d0d0f] border border-[#ea580c] text-[#ea580c] font-mono text-xs uppercase tracking-widest hover:bg-[#ea580c] hover:text-white transition-colors flex items-center gap-2"
          title="Cennik wewnętrzny — duża czcionka, do biura"
        >
          <Printer size={16}/> Drukuj wewnętrzny
        </button>
      </div>

      {showPrintModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="internal-print-modal" onClick={() => setShowPrintModal(false)}>
          <div className="bg-[#0d0d0f] border border-[#2a2a2e] max-w-2xl w-full max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 pb-4 border-b border-[#2a2a2e]">
              <div className="font-display text-xl uppercase text-white mb-1">Cennik wewnętrzny</div>
              <p className="text-xs text-[#a8a29e] leading-relaxed">
                Duża, czytelna czcionka. <b className="text-[#ea580c]">Jedna sekcja = jedna strona A4</b>.
                Możesz też dodać własną stronę i wybrać dokładnie te kategorie, które chcesz.
              </p>
            </div>

            <div className="overflow-y-auto flex-1 p-6 space-y-5">
              {/* Font size */}
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#a8a29e] mb-2">// Rozmiar czcionki</div>
                <div className="flex gap-2 flex-wrap">
                  {FONT_PRESETS.map((f) => (
                    <button
                      key={f.id}
                      onClick={() => setFontScale(f.scale)}
                      data-testid={`font-preset-${f.id}`}
                      className={`px-4 py-2 border font-mono text-xs uppercase tracking-widest transition-colors ${
                        fontScale === f.scale
                          ? "bg-[#ea580c] text-white border-[#ea580c]"
                          : "bg-[#0a0a0b] text-[#a8a29e] border-[#2a2a2e] hover:border-[#ea580c] hover:text-[#ea580c]"
                      }`}
                      style={{ fontSize: `${11 * f.scale}px` }}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Preset sections */}
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#a8a29e] mb-2">// Sekcje gotowe</div>
                <div className="space-y-2">
                  {INTERNAL_PRINT_SECTIONS.map((s) => (
                    <label key={s.id} className="flex items-start gap-3 p-3 bg-[#0a0a0b] border border-[#2a2a2e] hover:border-[#ea580c] cursor-pointer transition-colors" data-testid={`print-sec-${s.id}`}>
                      <input
                        type="checkbox"
                        checked={printSel.includes(s.id)}
                        onChange={() => togglePrintSection(s.id)}
                        className="mt-0.5 w-4 h-4 accent-[#ea580c] cursor-pointer"
                      />
                      <span className="text-sm text-white">{s.label}</span>
                    </label>
                  ))}
                </div>
                <div className="flex gap-3 mt-2">
                  <button onClick={() => setPrintSel(INTERNAL_PRINT_SECTIONS.map((s) => s.id))} className="text-xs font-mono uppercase tracking-widest text-[#a8a29e] hover:text-[#ea580c]" data-testid="print-select-all">Zaznacz wszystkie</button>
                  <span className="text-[#57534e]">·</span>
                  <button onClick={() => setPrintSel([])} className="text-xs font-mono uppercase tracking-widest text-[#a8a29e] hover:text-[#ea580c]" data-testid="print-select-none">Odznacz wszystkie</button>
                </div>
              </div>

              {/* Custom pages */}
              <div className="border-t border-[#2a2a2e] pt-5">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#a8a29e]">// Własne strony</div>
                    <p className="text-[10px] text-[#57534e] mt-1">Każda własna strona = kolejna strona A4 z wybranymi przez Ciebie kategoriami.</p>
                  </div>
                  <button onClick={addCustomPage} className="px-3 py-2 border border-[#ea580c] text-[#ea580c] text-xs font-mono uppercase tracking-widest hover:bg-[#ea580c] hover:text-white flex items-center gap-2" data-testid="add-custom-page-btn">
                    <Plus size={14}/> Dodaj stronę
                  </button>
                </div>

                {customPages.length === 0 && (
                  <div className="text-xs text-[#57534e] font-mono italic py-3">
                    Brak własnych stron. Kliknij „Dodaj stronę", by wybrać konkretne kategorie.
                  </div>
                )}

                {customPages.map((cp, idx) => (
                  <div key={cp.id} className="bg-[#0a0a0b] border border-[#2a2a2e] p-3 mb-3" data-testid={`custom-page-${idx}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-display text-sm uppercase text-white">Własna strona {idx + 1} <span className="text-[#57534e] text-xs font-mono normal-case tracking-normal">({cp.categoryIds.length} wybranych)</span></span>
                      <button onClick={() => removeCustomPage(cp.id)} className="p-1.5 text-[#a8a29e] hover:text-red-500" data-testid={`remove-custom-page-${idx}`}>
                        <Trash2 size={14}/>
                      </button>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-2 max-h-[280px] overflow-y-auto pr-1">
                      {[...(data.siatka || []), ...(data.panele || [])].map((c) => (
                        <label key={c.id} className="flex items-center gap-2 px-2 py-1.5 bg-[#131316] border border-transparent hover:border-[#ea580c] cursor-pointer text-xs text-[#d6d3d1]" data-testid={`custom-cat-${idx}-${c.id}`}>
                          <input
                            type="checkbox"
                            checked={cp.categoryIds.includes(c.id)}
                            onChange={() => toggleCustomCat(cp.id, c.id)}
                            className="w-3.5 h-3.5 accent-[#ea580c] cursor-pointer shrink-0"
                          />
                          <span className="truncate">{c.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-6 pt-4 border-t border-[#2a2a2e] flex gap-3 justify-end items-center">
              <button onClick={() => setShowPrintModal(false)} className="px-4 py-2 border border-[#2a2a2e] text-[#a8a29e] text-xs font-mono uppercase tracking-widest hover:text-white">Anuluj</button>
              <button
                onClick={runInternalPrint}
                disabled={totalPagesToPrint === 0}
                data-testid="print-confirm-btn"
                className="px-5 py-2 bg-[#ea580c] text-white text-xs font-mono uppercase tracking-widest hover:bg-[#c2410c] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Printer size={14}/> Drukuj ({totalPagesToPrint} {totalPagesToPrint === 1 ? 'strona' : 'stron'})
              </button>
            </div>
          </div>
        </div>
      )}

      {msg && <div className="px-4 py-2 mb-4 bg-[#131316] border border-[#ea580c] text-[#ea580c] text-sm font-mono">{msg}</div>}

      {cats.map((cat, ci) => (
        <div key={cat.id} className="border border-[#2a2a2e] mb-4 bg-[#0d0d0f]" data-testid={`admin-cat-${ci}`}>
          <div className="px-4 py-3 border-b border-[#2a2a2e] bg-[#131316] flex items-center gap-3">
            <input value={cat.name} onChange={(e) => updateCat(ci, {name: e.target.value})} className="flex-1 bg-transparent text-white font-display text-lg uppercase tracking-wider focus:outline-none" />
            <button onClick={() => move(ci, -1)} disabled={ci === 0 || busy} className="p-2 text-[#a8a29e] hover:text-[#ea580c] disabled:opacity-30" title="W górę"><ArrowUp size={16}/></button>
            <button onClick={() => move(ci, 1)} disabled={ci === cats.length - 1 || busy} className="p-2 text-[#a8a29e] hover:text-[#ea580c] disabled:opacity-30" title="W dół"><ArrowDown size={16}/></button>
            <button onClick={() => saveCat(ci)} disabled={busy} className="px-3 py-1.5 bg-[#ea580c] text-white text-xs uppercase tracking-widest font-mono hover:bg-[#c2410c]" data-testid={`save-cat-${ci}`}>Zapisz</button>
            <button onClick={() => deleteCat(ci)} disabled={busy} className="p-2 text-[#a8a29e] hover:text-red-500" title="Usuń"><Trash2 size={16}/></button>
          </div>
          <div className="p-4">
            {(cat.items || []).map((it, ii) => (
              <div key={ii} className="grid grid-cols-12 gap-2 mb-2">
                <input value={it.n} onChange={(e) => setItemField(ci, ii, "n", e.target.value)}
                       placeholder="Nazwa pozycji"
                       className="col-span-7 bg-[#131316] border border-[#2a2a2e] px-3 py-2 text-white text-sm focus:border-[#ea580c] focus:outline-none"/>
                <input value={it.p} onChange={(e) => setItemField(ci, ii, "p", e.target.value)}
                       placeholder="Cena"
                       className="col-span-4 bg-[#131316] border border-[#2a2a2e] px-3 py-2 text-[#ea580c] font-display text-base focus:border-[#ea580c] focus:outline-none"/>
                <button onClick={() => removeItem(ci, ii)} className="col-span-1 bg-[#131316] border border-[#2a2a2e] text-[#a8a29e] hover:text-red-500" title="Usuń pozycję"><Trash2 size={14} className="mx-auto"/></button>
              </div>
            ))}
            <button onClick={() => addItem(ci)} className="mt-2 text-sm text-[#ea580c] hover:underline font-mono uppercase tracking-widest"><Plus size={14} className="inline mr-1"/> Dodaj pozycję</button>
          </div>
        </div>
      ))}

      <button onClick={addCat} disabled={busy} className="btn-ghost mt-4" data-testid="add-category-btn">
        <Plus size={16}/> Dodaj nową kategorię
      </button>
    </div>
  );
};

/* ─────────── GALLERY EDITOR ─────────── */
const GALLERY_CATS = [
  { id: "realizacje", label: "Realizacje" },
  { id: "podmurowki", label: "Podmurówki" },
  { id: "siatki",     label: "Siatki" },
  { id: "slupki",     label: "Słupki" },
  { id: "panele",     label: "Panele" },
  { id: "inne",       label: "Inne" },
];

/* ──────────── Edytor Wariantów Konfiguratora ──────────── */
const VARIANT_DEFAULTS = {
  panelColors: [
    { id: "ral_7016", label: "Antracyt RAL 7016", isRal: true },
    { id: "ral_9005", label: "Czarny RAL 9005",   isRal: true },
    { id: "ral_6005", label: "Zielony RAL 6005",  isRal: true },
    { id: "ral_8017", label: "Brązowy RAL 8017",  isRal: true },
    { id: "ocynk",    label: "Ocynk",             isRal: false },
  ],
  podmColors: [
    { id: "jasna",  label: "Jasna" },
    { id: "grafit", label: "Grafit" },
  ],
  podmPatterns: { jasna: [], grafit: [] },
  lacznikTypes: [
    { id: "stalowy",  label: "Stalowy ceownik", n: "Łącznik stalowy ceownik RAL 25 cm", multiplier: 2 },
    { id: "betonowy", label: "Betonowy",        n: "Łącznik betonowy",                  multiplier: 1 },
  ],
};

const ConfigVariantsEditor = () => {
  const [v, setV] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.getConfig().then((cfg) => {
      const cur = cfg.configuratorVariants || {};
      setV({
        panelColors:  Array.isArray(cur.panelColors)  && cur.panelColors.length  ? cur.panelColors  : VARIANT_DEFAULTS.panelColors,
        podmColors:   Array.isArray(cur.podmColors)   && cur.podmColors.length   ? cur.podmColors   : VARIANT_DEFAULTS.podmColors,
        podmPatterns: cur.podmPatterns && Object.keys(cur.podmPatterns).length   ? cur.podmPatterns : VARIANT_DEFAULTS.podmPatterns,
        lacznikTypes: Array.isArray(cur.lacznikTypes) && cur.lacznikTypes.length ? cur.lacznikTypes : VARIANT_DEFAULTS.lacznikTypes,
      });
    });
    api.getPricing().then(setPricing);
  }, []);

  // Lista wszystkich pozycji z cennika (do <select> przy priceItem / n)
  const priceItemOptions = React.useMemo(() => {
    const all = [];
    if (pricing) {
      for (const cat of [...(pricing.siatka || []), ...(pricing.panele || [])]) {
        for (const it of (cat.items || [])) {
          all.push({ value: it.n, label: `${cat.name} → ${it.n}` });
        }
      }
    }
    return all;
  }, [pricing]);

  const save = async () => {
    setSaving(true); setMsg("");
    try {
      const cfg = await api.getConfig();
      await api.saveConfig({ ...cfg, configuratorVariants: v });
      setMsg("✓ Zapisano warianty konfiguratora");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) {
      setMsg("Błąd: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  if (!v || !pricing) return <div className="py-12"><Loader2 className="animate-spin"/></div>;

  // Helpery edycji list
  const updList = (key, fn) => setV((cur) => ({ ...cur, [key]: fn(cur[key] || []) }));
  const updPatterns = (colorId, fn) => setV((cur) => ({
    ...cur,
    podmPatterns: { ...cur.podmPatterns, [colorId]: fn(cur.podmPatterns[colorId] || []) },
  }));

  return (
    <div data-testid="config-variants-editor" className="space-y-8">
      <div className="bg-[#0d0d0f] border border-[#2a2a2e] p-4 text-sm text-[#a8a29e]">
        Tutaj edytujesz <b className="text-white">warianty wybierane w konfiguratorze</b> — kolory paneli, kolory i wzory podmurówek, oraz typy łączników. Każdy wariant musi być powiązany z pozycją w cenniku, by konfigurator mógł wyliczyć cenę.
        <br/><span className="text-[#ea580c]">Tip:</span> jeśli dodajesz nowy wzór podmurówki — najpierw dodaj jego pozycję w zakładce „Cennik", dopiero potem wybierz ją tutaj z listy.
      </div>

      {/* ─── Kolory paneli ─── */}
      <Section title="Kolory paneli (Krok 2)" defaultOpen>
        <div className="space-y-2">
          {v.panelColors.map((c, i) => (
            <div key={i} className="flex flex-wrap gap-2 items-center bg-[#0d0d0f] border border-[#2a2a2e] p-3">
              <input
                value={c.label}
                onChange={(e) => updList("panelColors", (l) => l.map((x, k) => k === i ? { ...x, label: e.target.value } : x))}
                placeholder="np. Antracyt RAL 7016"
                className="flex-1 min-w-[200px] bg-[#0a0a0b] border border-[#2a2a2e] px-3 py-2 text-sm text-white"
                data-testid={`pc-label-${i}`}
              />
              <label className="flex items-center gap-2 text-xs text-[#a8a29e] font-mono uppercase tracking-widest">
                <input
                  type="checkbox"
                  checked={!!c.isRal}
                  onChange={(e) => updList("panelColors", (l) => l.map((x, k) => k === i ? { ...x, isRal: e.target.checked } : x))}
                /> RAL (malowany)
              </label>
              <button onClick={() => updList("panelColors", (l) => l.filter((_, k) => k !== i))} className="text-[#ea580c] hover:bg-[#ea580c]/10 p-2" data-testid={`pc-del-${i}`}><Trash2 size={16}/></button>
            </div>
          ))}
          <button
            onClick={() => updList("panelColors", (l) => [...l, { id: `kolor_${Date.now()}`, label: "Nowy kolor", isRal: true }])}
            className="text-xs font-mono uppercase tracking-widest text-[#ea580c] hover:underline"
            data-testid="pc-add"
          ><Plus size={14} className="inline"/> Dodaj kolor</button>
        </div>
      </Section>

      {/* ─── Kolory podmurówek ─── */}
      <Section title="Kolory podmurówek">
        <div className="space-y-2">
          {v.podmColors.map((c, i) => (
            <div key={i} className="flex flex-wrap gap-2 items-center bg-[#0d0d0f] border border-[#2a2a2e] p-3">
              <input
                value={c.label}
                onChange={(e) => updList("podmColors", (l) => l.map((x, k) => k === i ? { ...x, label: e.target.value } : x))}
                placeholder="np. Jasna"
                className="flex-1 min-w-[200px] bg-[#0a0a0b] border border-[#2a2a2e] px-3 py-2 text-sm text-white"
              />
              <span className="text-xs text-[#a8a29e] font-mono">{c.id}</span>
              <button
                onClick={() => {
                  if (!window.confirm(`Usunąć kolor "${c.label}" wraz z jego wzorami?`)) return;
                  updList("podmColors", (l) => l.filter((_, k) => k !== i));
                  setV((cur) => {
                    const np = { ...cur.podmPatterns };
                    delete np[c.id];
                    return { ...cur, podmPatterns: np };
                  });
                }}
                className="text-[#ea580c] hover:bg-[#ea580c]/10 p-2"
              ><Trash2 size={16}/></button>
            </div>
          ))}
          <button
            onClick={() => {
              const id = `kolor_${Date.now()}`;
              updList("podmColors", (l) => [...l, { id, label: "Nowy kolor" }]);
              setV((cur) => ({ ...cur, podmPatterns: { ...cur.podmPatterns, [id]: [] } }));
            }}
            className="text-xs font-mono uppercase tracking-widest text-[#ea580c] hover:underline"
          ><Plus size={14} className="inline"/> Dodaj kolor podmurówki</button>
        </div>
      </Section>

      {/* ─── Wzory podmurówek (per kolor) ─── */}
      <Section title="Wzory podmurówek (powiązane z cennikiem)" defaultOpen>
        <div className="space-y-6">
          {v.podmColors.map((c) => (
            <div key={c.id} className="bg-[#0d0d0f] border border-[#2a2a2e] p-4">
              <div className="font-display text-lg uppercase text-[#ea580c] mb-3">{c.label}</div>
              <div className="space-y-2">
                {(v.podmPatterns[c.id] || []).map((p, i) => (
                  <div key={i} className="flex flex-wrap gap-2 items-center bg-[#0a0a0b] border border-[#2a2a2e] p-3" data-testid={`pp-${c.id}-${i}`}>
                    <input
                      value={p.label}
                      onChange={(e) => updPatterns(c.id, (l) => l.map((x, k) => k === i ? { ...x, label: e.target.value } : x))}
                      placeholder="np. Modern / palisada"
                      className="flex-1 min-w-[200px] bg-[#131316] border border-[#2a2a2e] px-3 py-2 text-sm text-white"
                    />
                    <select
                      value={p.priceItem || ""}
                      onChange={(e) => updPatterns(c.id, (l) => l.map((x, k) => k === i ? { ...x, priceItem: e.target.value } : x))}
                      className="flex-1 min-w-[260px] bg-[#131316] border border-[#2a2a2e] px-3 py-2 text-sm text-white"
                      data-testid={`pp-priceitem-${c.id}-${i}`}
                    >
                      <option value="">— wybierz pozycję cennika —</option>
                      {priceItemOptions.map((opt) => (
                        <option key={opt.value + opt.label} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <button onClick={() => updPatterns(c.id, (l) => l.filter((_, k) => k !== i))} className="text-[#ea580c] hover:bg-[#ea580c]/10 p-2"><Trash2 size={16}/></button>
                  </div>
                ))}
                <button
                  onClick={() => updPatterns(c.id, (l) => [...l, { id: `wzor_${Date.now()}`, label: "Nowy wzór", priceItem: "" }])}
                  className="text-xs font-mono uppercase tracking-widest text-[#ea580c] hover:underline"
                  data-testid={`pp-add-${c.id}`}
                ><Plus size={14} className="inline"/> Dodaj wzór dla „{c.label}"</button>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ─── Typy łączników ─── */}
      <Section title="Typy łączników podmurówek">
        <div className="space-y-2">
          {v.lacznikTypes.map((l, i) => (
            <div key={i} className="bg-[#0d0d0f] border border-[#2a2a2e] p-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-center" data-testid={`lt-${i}`}>
              <input
                value={l.label}
                onChange={(e) => updList("lacznikTypes", (lst) => lst.map((x, k) => k === i ? { ...x, label: e.target.value } : x))}
                placeholder="Nazwa wyświetlana"
                className="md:col-span-3 bg-[#0a0a0b] border border-[#2a2a2e] px-3 py-2 text-sm text-white"
              />
              <select
                value={l.n || ""}
                onChange={(e) => updList("lacznikTypes", (lst) => lst.map((x, k) => k === i ? { ...x, n: e.target.value } : x))}
                className="md:col-span-6 bg-[#0a0a0b] border border-[#2a2a2e] px-3 py-2 text-sm text-white"
                data-testid={`lt-priceitem-${i}`}
              >
                <option value="">— wybierz pozycję cennika —</option>
                {priceItemOptions.map((opt) => (
                  <option key={opt.value + opt.label} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <input
                type="number"
                step="0.5"
                min="0"
                value={l.multiplier ?? 1}
                onChange={(e) => updList("lacznikTypes", (lst) => lst.map((x, k) => k === i ? { ...x, multiplier: parseFloat(e.target.value) || 0 } : x))}
                placeholder="× szt./podmurówkę"
                className="md:col-span-2 bg-[#0a0a0b] border border-[#2a2a2e] px-3 py-2 text-sm text-white"
                title="Ilość łączników na 1 podmurówkę. Stalowy = 2, Betonowy = 1."
              />
              <button onClick={() => updList("lacznikTypes", (lst) => lst.filter((_, k) => k !== i))} className="md:col-span-1 text-[#ea580c] hover:bg-[#ea580c]/10 p-2"><Trash2 size={16}/></button>
            </div>
          ))}
          <button
            onClick={() => updList("lacznikTypes", (lst) => [...lst, { id: `lacznik_${Date.now()}`, label: "Nowy", n: "", multiplier: 1 }])}
            className="text-xs font-mono uppercase tracking-widest text-[#ea580c] hover:underline"
          ><Plus size={14} className="inline"/> Dodaj typ łącznika</button>
        </div>
      </Section>

      {/* Save bar */}
      <div className="sticky bottom-0 z-10 -mx-6 px-6 py-4 bg-[#0d0d0f] border-t border-[#2a2a2e] flex items-center gap-4 mt-8">
        <button
          onClick={save}
          disabled={saving}
          className="btn-primary"
          data-testid="variants-save-btn"
        >
          {saving ? <Loader2 className="animate-spin" size={16}/> : <Save size={16}/>}
          Zapisz warianty
        </button>
        {msg && <span className="text-sm font-mono text-[#ea580c]">{msg}</span>}
        <span className="ml-auto text-xs text-[#a8a29e] font-mono uppercase tracking-widest hidden sm:inline">
          Po zapisie zmiany od razu widoczne na stronie konfiguratora
        </span>
      </div>
    </div>
  );
};

const GalleryEditor = () => {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const fileRef = useRef(null);

  const reload = () => api.getGallery().then(setItems);
  useEffect(() => { reload(); }, []);

  const setField = (idx, field, value) => {
    setItems((cur) => cur.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  };

  const save = async (idx) => {
    const it = items[idx];
    setBusy(true); setMsg("");
    try {
      await api.updateGalleryItem(it.id, { src: it.src, thumb: it.thumb, category: it.category, caption: it.caption });
      setMsg("✓ Zapisano: " + (it.caption || it.src));
      setTimeout(() => setMsg(""), 2500);
    } catch (e) {
      setMsg("Błąd: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const remove = async (idx) => {
    const it = items[idx];
    if (!window.confirm(`Usunąć zdjęcie „${it.caption || it.src}"?`)) return;
    setBusy(true); setMsg("");
    try {
      await api.deleteGalleryItem(it.id);
      setItems((cur) => cur.filter((_, i) => i !== idx));
      setMsg("✓ Usunięto");
      setTimeout(() => setMsg(""), 2000);
    } catch (e) {
      setMsg("Błąd: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const move = async (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= items.length) return;
    const reordered = [...items];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    setItems(reordered);
    try { await api.reorderGallery(reordered.map((g) => g.id)); }
    catch (e) { setMsg("Błąd zmiany kolejności"); }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true); setMsg("");
    try {
      for (const file of files) {
        const r = await api.uploadImage(file);
        await api.addGalleryItem({ src: r.url, thumb: r.thumb || r.url, category: "inne", caption: file.name.replace(/\.[^.]+$/, "") });
      }
      await reload();
      setMsg(`✓ Dodano ${files.length} zdjęć — uzupełnij opisy i kategorie`);
      setTimeout(() => setMsg(""), 3500);
    } catch (e) {
      setMsg("Błąd uploadu: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (!items) return <Loader2 className="animate-spin"/>;

  return (
    <div data-testid="gallery-editor">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="btn-primary"
          data-testid="gallery-upload-btn"
        >
          {busy ? <Loader2 className="animate-spin" size={16}/> : <Upload size={16}/>}
          Wgraj zdjęcia (możesz zaznaczyć kilka)
        </button>
        <button
          type="button"
          onClick={async () => {
            if (!window.confirm("Konwertuje stare pliki HEIC (z iPhone) na JPG. Może potrwać do 1 minuty. Kontynuować?")) return;
            setBusy(true);
            try {
              const r = await api.repairGalleryHeic();
              setMsg(`✓ Naprawa: skonwertowano ${r.converted} zdjęć, aktualizacji w bazie: ${r.db_rewrites}${r.failed ? `, błędów: ${r.failed}` : ""}`);
              await reload();
            } catch (e) {
              setMsg("✗ Błąd naprawy: " + (e?.response?.data?.detail || e.message));
            } finally {
              setBusy(false);
            }
          }}
          disabled={busy}
          className="px-4 py-2 border border-[#2a2a2e] bg-[#131316] hover:border-[#ea580c] hover:text-[#ea580c] text-[#d6d3d1] text-sm font-mono uppercase tracking-widest transition-colors"
          data-testid="gallery-repair-heic-btn"
          title="Konwertuje stare pliki HEIC z iPhone na JPG i aktualizuje bazę"
        >
          Napraw stare HEIC
        </button>
        <span className="text-xs text-[#a8a29e] font-mono uppercase tracking-widest">
          {items.length} {items.length === 1 ? "zdjęcie" : "zdjęć"}
        </span>
      </div>

      {msg && (
        <div className="px-4 py-2 mb-4 bg-[#131316] border border-[#ea580c] text-[#ea580c] text-sm font-mono">{msg}</div>
      )}

      <div className="space-y-3">
        {items.map((it, i) => (
          <div key={it.id} className="border border-[#2a2a2e] bg-[#0d0d0f] flex flex-col md:flex-row gap-4 p-4" data-testid={`gallery-item-${i}`}>
            {/* Thumb */}
            <div className="w-full md:w-32 h-32 shrink-0 bg-[#131316] border border-[#2a2a2e] overflow-hidden">
              <img src={resolveImageUrl(it.thumb || it.src)} alt={it.caption} className="w-full h-full object-cover"/>
            </div>

            {/* Fields */}
            <div className="flex-1 grid sm:grid-cols-12 gap-3 items-start">
              <div className="sm:col-span-6">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#a8a29e] mb-1">Opis (caption)</div>
                <input
                  value={it.caption || ""}
                  onChange={(e) => setField(i, "caption", e.target.value)}
                  placeholder="np. Podmurówka — wzór modern, kolor jasny"
                  className="w-full bg-[#131316] border border-[#2a2a2e] px-3 py-2 text-white text-sm focus:border-[#ea580c] focus:outline-none"
                  data-testid={`gallery-caption-${i}`}
                />
              </div>
              <div className="sm:col-span-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#a8a29e] mb-1">Kategoria</div>
                <select
                  value={it.category || "inne"}
                  onChange={(e) => setField(i, "category", e.target.value)}
                  className="w-full bg-[#131316] border border-[#2a2a2e] px-3 py-2 text-white text-sm focus:border-[#ea580c] focus:outline-none"
                  data-testid={`gallery-category-${i}`}
                >
                  {GALLERY_CATS.map((c) => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-3 flex items-end gap-2 justify-end h-full">
                <button onClick={() => move(i, -1)} disabled={i === 0 || busy} className="p-2 bg-[#131316] border border-[#2a2a2e] text-[#a8a29e] hover:text-[#ea580c] disabled:opacity-30" title="W górę"><ArrowUp size={16}/></button>
                <button onClick={() => move(i, 1)} disabled={i === items.length - 1 || busy} className="p-2 bg-[#131316] border border-[#2a2a2e] text-[#a8a29e] hover:text-[#ea580c] disabled:opacity-30" title="W dół"><ArrowDown size={16}/></button>
                <button
                  onClick={() => save(i)}
                  disabled={busy}
                  className="px-3 py-2 bg-[#ea580c] text-white text-xs uppercase tracking-widest font-mono hover:bg-[#c2410c]"
                  data-testid={`gallery-save-${i}`}
                >
                  Zapisz
                </button>
                <button
                  onClick={() => remove(i)}
                  disabled={busy}
                  className="p-2 bg-[#131316] border border-[#2a2a2e] text-[#a8a29e] hover:text-red-500 hover:border-red-500"
                  title="Usuń"
                  data-testid={`gallery-delete-${i}`}
                >
                  <Trash2 size={16}/>
                </button>
              </div>
            </div>
          </div>
        ))}

        {items.length === 0 && (
          <div className="text-center py-12 text-[#a8a29e] border border-dashed border-[#2a2a2e]">
            Brak zdjęć w galerii. Kliknij „Wgraj zdjęcia" powyżej.
          </div>
        )}
      </div>
    </div>
  );
};

/* ─────────── PRODUCT PAGES EDITOR (intro + FAQ tylko) ─────────── */
const ProductPagesEditor = () => {
  const [config, setConfig] = useState(null);
  const [slug, setSlug] = useState("");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.getConfig().then((cfg) => {
      setConfig(cfg);
      const slugs = Object.keys(cfg?.productPages || {});
      if (slugs.length) setSlug(slugs[0]);
    });
  }, []);

  useEffect(() => {
    if (!config || !slug) return;
    const page = (config.productPages || {})[slug];
    // Deep clone tylko edytowalnych pól (intro + faq)
    setDraft({
      intro: page?.intro || "",
      faq: (page?.faq || []).map((f) => ({ q: f.q || "", a: f.a || "" })),
    });
  }, [config, slug]);

  const updateFaq = (idx, key, val) => {
    setDraft((d) => ({ ...d, faq: d.faq.map((f, i) => (i === idx ? { ...f, [key]: val } : f)) }));
  };
  const addFaq = () => setDraft((d) => ({ ...d, faq: [...d.faq, { q: "", a: "" }] }));
  const removeFaq = (idx) => setDraft((d) => ({ ...d, faq: d.faq.filter((_, i) => i !== idx) }));
  const moveFaq = (idx, dir) => {
    setDraft((d) => {
      const arr = [...d.faq];
      const ni = idx + dir;
      if (ni < 0 || ni >= arr.length) return d;
      [arr[idx], arr[ni]] = [arr[ni], arr[idx]];
      return { ...d, faq: arr };
    });
  };

  const save = async () => {
    if (!config || !slug || !draft) return;
    setSaving(true); setMsg("");
    try {
      const merged = JSON.parse(JSON.stringify(config));
      merged.productPages = merged.productPages || {};
      merged.productPages[slug] = {
        ...(merged.productPages[slug] || {}),
        intro: draft.intro,
        faq: draft.faq.filter((f) => f.q.trim() || f.a.trim()),
      };
      await api.saveConfig(merged);
      setConfig(merged);
      setMsg("✓ Zapisano");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) {
      setMsg("Błąd: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  if (!config) return <Loader2 className="animate-spin"/>;
  const slugs = Object.keys(config.productPages || {});
  const currentPage = (config.productPages || {})[slug] || {};

  return (
    <div data-testid="product-pages-editor" className="space-y-6">
      <div className="border border-[#2a2a2e] bg-[#0d0d0f] p-5">
        <div className="font-display text-lg uppercase text-white mb-1">Edycja podstron produktów</div>
        <p className="text-xs text-[#a8a29e] mb-4 leading-relaxed">
          Możesz edytować <b className="text-[#d6d3d1]">opis (intro)</b> i <b className="text-[#d6d3d1]">FAQ</b> każdej z 6 podstron.
          Tytuł, lead, tabela wariantów, meta SEO oraz CTA są ustawione na stałe.
        </p>
        <label className="block">
          <span className="block text-[10px] font-mono uppercase tracking-widest text-[#a8a29e] mb-1">Wybierz podstronę</span>
          <select
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            data-testid="product-page-select"
            className="w-full bg-[#131316] border border-[#2a2a2e] px-3 py-2.5 text-white text-sm focus:border-[#ea580c] focus:outline-none"
          >
            {slugs.map((s) => (
              <option key={s} value={s}>
                {(config.productPages[s]?.title) || s} — /produkty/{s}
              </option>
            ))}
          </select>
        </label>
      </div>

      {draft && (
        <>
          {/* Read-only preview */}
          <div className="border border-[#2a2a2e] bg-[#0a0a0b]/60 p-5">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#57534e] mb-2">// Stałe (nie do edycji)</div>
            <div className="text-sm text-[#d6d3d1]"><span className="text-[#a8a29e] font-mono text-xs">Tytuł:</span> {currentPage.title}</div>
            <div className="text-sm text-[#d6d3d1] mt-1"><span className="text-[#a8a29e] font-mono text-xs">Lead:</span> {currentPage.lead}</div>
            <a href={`/produkty/${slug}`} target="_blank" rel="noopener noreferrer"
               className="inline-block mt-3 text-xs font-mono uppercase tracking-widest text-[#ea580c] hover:underline">
              Otwórz podstronę ↗
            </a>
          </div>

          {/* Intro */}
          <div className="border border-[#2a2a2e] bg-[#0d0d0f] p-5">
            <div className="font-display text-base uppercase text-white mb-1">Opis (intro)</div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-[#57534e] mb-3">
              Akapity rozdziel <span className="text-[#ea580c]">pustą linią</span> (dwa Entery). Mogą być wypunktowania zaczynające się od „•".
            </p>
            <textarea
              value={draft.intro}
              onChange={(e) => setDraft({ ...draft, intro: e.target.value })}
              rows={14}
              data-testid="product-page-intro"
              className="w-full bg-[#131316] border border-[#2a2a2e] px-3 py-2 text-white text-sm focus:border-[#ea580c] focus:outline-none font-mono leading-relaxed"
            />
          </div>

          {/* FAQ */}
          <div className="border border-[#2a2a2e] bg-[#0d0d0f] p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-display text-base uppercase text-white">FAQ — pytania i odpowiedzi</div>
                <p className="text-[10px] font-mono uppercase tracking-widest text-[#57534e] mt-1">
                  Trafiają do schema.org FAQPage — dobre dla SEO.
                </p>
              </div>
              <button onClick={addFaq} className="btn-ghost" data-testid="add-faq-btn">
                <Plus size={14}/> Dodaj pytanie
              </button>
            </div>

            <div className="space-y-4">
              {draft.faq.map((f, i) => (
                <div key={i} className="border border-[#2a2a2e] bg-[#0a0a0b] p-4" data-testid={`faq-item-${i}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[#ea580c]">// #{i + 1}</span>
                    <div className="flex gap-1">
                      <button onClick={() => moveFaq(i, -1)} disabled={i === 0} className="p-1.5 text-[#a8a29e] hover:text-[#ea580c] disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`faq-up-${i}`}>
                        <ArrowUp size={14}/>
                      </button>
                      <button onClick={() => moveFaq(i, 1)} disabled={i === draft.faq.length - 1} className="p-1.5 text-[#a8a29e] hover:text-[#ea580c] disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`faq-down-${i}`}>
                        <ArrowDown size={14}/>
                      </button>
                      <button onClick={() => removeFaq(i)} className="p-1.5 text-[#a8a29e] hover:text-red-500" data-testid={`faq-remove-${i}`}>
                        <Trash2 size={14}/>
                      </button>
                    </div>
                  </div>
                  <input
                    value={f.q}
                    onChange={(e) => updateFaq(i, "q", e.target.value)}
                    placeholder="Pytanie"
                    data-testid={`faq-q-${i}`}
                    className="w-full bg-[#131316] border border-[#2a2a2e] px-3 py-2 text-white text-sm focus:border-[#ea580c] focus:outline-none mb-2"
                  />
                  <textarea
                    value={f.a}
                    onChange={(e) => updateFaq(i, "a", e.target.value)}
                    rows={3}
                    placeholder="Odpowiedź"
                    data-testid={`faq-a-${i}`}
                    className="w-full bg-[#131316] border border-[#2a2a2e] px-3 py-2 text-white text-sm focus:border-[#ea580c] focus:outline-none leading-relaxed"
                  />
                </div>
              ))}
              {draft.faq.length === 0 && (
                <div className="text-xs text-[#57534e] font-mono italic text-center py-6">
                  Brak pytań. Kliknij „Dodaj pytanie" powyżej.
                </div>
              )}
            </div>
          </div>

          {/* Save */}
          <div className="sticky bottom-0 bg-[#0a0a0b] border-t border-[#2a2a2e] -mx-6 px-6 py-3 flex items-center justify-end gap-3">
            {msg && <span className={`text-sm font-mono ${msg.startsWith("✓") ? "text-[#22c55e]" : "text-red-400"}`} data-testid="product-pages-save-msg">{msg}</span>}
            <button onClick={save} disabled={saving} className="btn-primary" data-testid="product-pages-save-btn">
              {saving ? <Loader2 className="animate-spin" size={16}/> : <Save size={16}/>} Zapisz
            </button>
          </div>
        </>
      )}
    </div>
  );
};

/* ─────────── ADMIN DASHBOARD ─────────── */
export const AdminDashboard = () => {
  const nav = useNavigate();
  const [auth, setAuth] = useState(null); // null=loading, false=no, true=yes
  const [view, setView] = useState("cennik");
  const [config, setConfig] = useState(null);
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    if (!getToken()) { setAuth(false); return; }
    api.me().then(() => setAuth(true)).catch(() => { clearToken(); setAuth(false); });
  }, []);

  useEffect(() => {
    if (!auth) return;
    api.getConfig().then(setConfig);
    api.getPricing().then(setPricing);
  }, [auth]);

  const reloadPricing = () => api.getPricing().then(setPricing);

  if (auth === null) return <div className="min-h-screen bg-[#0a0a0b] text-white flex items-center justify-center"><Loader2 className="animate-spin"/></div>;
  if (auth === false) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white">
      <header className="border-b border-[#2a2a2e] bg-[#0d0d0f] sticky top-0 z-40">
        <div className="max-w-[1300px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-[#ea580c] flex items-center justify-center font-display text-white text-lg">OF</div>
            <div>
              <div className="font-display text-white text-xl uppercase tracking-wider">Panel Admina</div>
              <div className="text-[10px] text-[#a8a29e] font-mono uppercase tracking-widest">Ogrodzenia Fajsławice</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noopener noreferrer" className="text-sm text-[#a8a29e] hover:text-[#ea580c] font-mono uppercase tracking-widest" data-testid="view-site-link">Zobacz stronę ↗</a>
            <button onClick={() => { clearToken(); nav("/admin/login"); }} className="btn-ghost" data-testid="logout-btn"><LogOut size={16}/> Wyloguj</button>
          </div>
        </div>
        <div className="max-w-[1300px] mx-auto px-6 flex gap-px bg-[#2a2a2e]">
          <button onClick={() => setView("cennik")} className={`px-6 py-3 font-mono text-xs uppercase tracking-widest ${view === "cennik" ? "bg-[#0a0a0b] text-[#ea580c] border-t-2 border-[#ea580c]" : "bg-[#0d0d0f] text-[#a8a29e]"}`} data-testid="view-cennik">Cennik</button>
          <button onClick={() => setView("galeria")} className={`px-6 py-3 font-mono text-xs uppercase tracking-widest ${view === "galeria" ? "bg-[#0a0a0b] text-[#ea580c] border-t-2 border-[#ea580c]" : "bg-[#0d0d0f] text-[#a8a29e]"}`} data-testid="view-galeria">Galeria</button>
          <button onClick={() => setView("warianty")} className={`px-6 py-3 font-mono text-xs uppercase tracking-widest ${view === "warianty" ? "bg-[#0a0a0b] text-[#ea580c] border-t-2 border-[#ea580c]" : "bg-[#0d0d0f] text-[#a8a29e]"}`} data-testid="view-warianty">Warianty</button>
          <button onClick={() => setView("tresci")} className={`px-6 py-3 font-mono text-xs uppercase tracking-widest ${view === "tresci" ? "bg-[#0a0a0b] text-[#ea580c] border-t-2 border-[#ea580c]" : "bg-[#0d0d0f] text-[#a8a29e]"}`} data-testid="view-tresci">Treści strony</button>
          <button onClick={() => setView("podstrony")} className={`px-6 py-3 font-mono text-xs uppercase tracking-widest ${view === "podstrony" ? "bg-[#0a0a0b] text-[#ea580c] border-t-2 border-[#ea580c]" : "bg-[#0d0d0f] text-[#a8a29e]"}`} data-testid="view-podstrony">Podstrony</button>
        </div>
      </header>

      <main className="max-w-[1300px] mx-auto px-6 py-8">
        <DownloadStats />
        {view === "cennik" && pricing && <PricingEditor initial={pricing} onChanged={reloadPricing}/>}
        {view === "galeria" && <GalleryEditor />}
        {view === "warianty" && <ConfigVariantsEditor />}
        {view === "tresci" && config && <ConfigEditor config={config} onSave={async (d) => { await api.saveConfig(d); }}/>}
        {view === "podstrony" && <ProductPagesEditor />}
        {(view === "cennik" && !pricing) || (view === "tresci" && !config) ? <Loader2 className="animate-spin"/> : null}
      </main>
    </div>
  );
};
